import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FiTag, FiPlus, FiEdit, FiTrash2, FiEye,
  FiEyeOff, FiGrid, FiList, FiChevronLeft, FiChevronRight,
  FiUpload, FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

// ✅ Icônes style photo (FontAwesome solid) via helper
import { renderCategoryIcon } from '../../utils/categoryIcons';
import { normalizeImagePath } from '../../utils/imageUtils';

const AdminCategoriesManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    descriptionAr: '',
    icon: '',
    active: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Photo de la categorie : le fichier choisi, l'apercu affiche, et le drapeau
  // d'effacement. Trois etats distincts et non un seul : « pas de nouveau
  // fichier » et « supprimer la photo » sont deux intentions differentes.
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const imageInputRef = useRef(null);

  const mountedRef = useRef(true);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const normalizeListFromResponse = (payload) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.docs)) return payload.docs;
    if (Array.isArray(payload.data?.data)) return payload.data.data;
    if (Array.isArray(payload.items)) return payload.items;
    return [];
  };

  const readTotalFromResponse = (axiosResponse) => {
    try {
      const res = axiosResponse?.data ?? {};
      const headers = axiosResponse?.headers || {};
      if (res && (typeof res.total === 'number' || typeof res.total === 'string')) return Number(res.total);
      if (res && res.meta && (typeof res.meta.total === 'number' || typeof res.meta.total === 'string')) return Number(res.meta.total);
      if (res && res.pagination && (typeof res.pagination.total === 'number' || typeof res.pagination.total === 'string')) return Number(res.pagination.total);
      if (res && res.totalItems) return Number(res.totalItems);
      if (headers && headers['x-total-count']) {
        const n = Number(headers['x-total-count']);
        if (!Number.isNaN(n)) return n;
      }
    } catch (e) {}
    return 0;
  };

  const fetchCategories = async (pageToFetch = 1, limitToFetch = 20) => {
    setLoading(true);
    try {
      const params = {
        page: pageToFetch,
        limit: limitToFetch,
        active: 'all'
      };

      const res = await API.get('/categories', { params });
      const payload = res?.data ?? {};

      let list = normalizeListFromResponse(payload);
      if (list.length === 0 && payload.data && Array.isArray(payload.data?.data)) {
        list = payload.data.data;
      }
      if (list.length === 0 && Array.isArray(payload)) list = payload;

      if (!mountedRef.current) return;
      setCategories(list || []);

      const totalItems =
        readTotalFromResponse(res) ||
        (payload?.meta?.total ?? payload?.pagination?.total ?? 0);

      if (totalItems) setTotal(Number(totalItems));
      else setTotal(list.length || 0);

      const limitFromServer =
        payload?.limit ??
        payload?.meta?.limit ??
        payload?.pagination?.limit ??
        payload?.itemsPerPage;

      if (limitFromServer) setPerPage(Number(limitFromServer));

      const pageFromServer =
        payload?.page ??
        payload?.currentPage ??
        payload?.meta?.currentPage ??
        payload?.pagination?.currentPage;

      if (pageFromServer) setPage(Number(pageFromServer));
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
      if (mountedRef.current) setCategories([]);
      setTotal(0);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  // L'API renvoie `isActive` (nom de la colonne). Tout l'ecran lisait
  // `category.active`, qui n'existe pas : une categorie desactivee s'affichait
  // donc comme active, et le compteur « Inactives » restait a 0 en permanence.
  // Un seul lecteur ici, pour que le probleme ne puisse pas revenir par un
  // endroit oublie.
  const isCatActive = (c) => (c?.isActive ?? c?.active) !== false;

  const resetImageState = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Garde-fou cote navigateur : multer refuse deja au-dela de 5 Mo, mais
    // autant le dire tout de suite plutot qu'apres l'upload.
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (5 Mo maximum)');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setRemoveImage(false);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setRemoveImage(true);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // ✅ on stocke icon comme string (clé)
      // Les champs sont envoyes EXPLICITEMENT (chaine vide incluse) : c'est ce
      // qui permet d'effacer une description. `...formData` suffisait tant que
      // toutes les cles existaient — mais handleEdit n'en renseignait qu'une
      // partie, et une cle `undefined` disparait a la serialisation JSON. La
      // valeur n'etait alors jamais envoyee, donc jamais enregistree.
      const payloadToSend = {
        name: (formData.name || '').trim(),
        nameAr: (formData.nameAr || '').trim(),
        description: (formData.description || '').trim(),
        descriptionAr: (formData.descriptionAr || '').trim(),
        icon: String(formData.icon || '').toLowerCase().trim(),
        // La colonne s'appelle `isActive`. Le formulaire envoyait `active`, que
        // le serveur ignorait : la case a cocher n'avait aucun effet.
        isActive: formData.active !== false,
        active: formData.active !== false
      };

      // Multipart uniquement quand il y a une photo a envoyer ou a effacer.
      // Le reste du temps on garde le JSON : `upload.single` cote serveur ignore
      // les requetes non-multipart, mais inutile de tout convertir en chaines.
      const body = (() => {
        if (!imageFile && !removeImage) return payloadToSend;
        const fd = new FormData();
        Object.entries(payloadToSend).forEach(([k, v]) => fd.append(k, v));
        if (imageFile) fd.append('image', imageFile);
        if (removeImage && !imageFile) fd.append('removeImage', '1');
        return fd;
      })();

      if (editingCategory) {
        await API.put(`/categories/${editingCategory._id}`, body);
        toast.success('Catégorie mise à jour');
      } else {
        await API.post('/categories', body);
        toast.success('Catégorie créée');
      }

      setShowModal(false);
      setEditingCategory(null);
      resetImageState();
      setFormData({ name: '', nameAr: '', description: '', descriptionAr: '', icon: '', active: true });
      setPage(1);
      await fetchCategories(1, perPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erreur lors de l'enregistrement");
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      // `nameAr` et `descriptionAr` etaient ABSENTS d'ici : le formulaire
      // s'ouvrait avec des valeurs `undefined`, l'admin retapait le nom arabe,
      // et la cle sautait a l'envoi. C'est la cause du « rien ne s'enregistre ».
      nameAr: category.nameAr || '',
      description: category.description || '',
      descriptionAr: category.descriptionAr || '',
      icon: category.icon || '',
      // La colonne renvoyee par l'API est `isActive`. `category.active` n'existe
      // pas : la case etait donc toujours cochee, quel que soit l'etat reel.
      active: isCatActive(category)
    });
    setImageFile(null);
    setRemoveImage(false);
    setImagePreview(category.image ? normalizeImagePath(category.image) : null);
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    if (!categoryId) return;
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie?')) return;

    try {
      await API.delete(`/categories/${categoryId}`);
      toast.success('Catégorie supprimée');
      await fetchCategories(page, perPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (categoryId) => {
    try {
      await API.patch(`/categories/${categoryId}/toggle`);
      toast.success('Statut mis à jour');
      await fetchCategories(page, perPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erreur lors de la mise à jour');
    }
  };

  const stats = {
    total: total || categories.length,
    active: categories.filter((c) => isCatActive(c)).length,
    inactive: categories.filter((c) => !isCatActive(c)).length
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || categories.length) / perPage)),
    [total, categories.length, perPage]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestion des Catégories</h1>
            <p className="text-gray-600 mt-1">Organisez et gérez les catégories de deals</p>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex bg-gray-100 rounded-lg p-1" role="tablist" aria-label="Mode d'affichage">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
                aria-pressed={viewMode === 'grid'}
                title="Vue grille"
              >
                <FiGrid />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
                aria-pressed={viewMode === 'list'}
                title="Vue liste"
              >
                <FiList />
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                setEditingCategory(null);
                setFormData({ name: '', nameAr: '', description: '', descriptionAr: '', icon: '', active: true });
                resetImageState();
                setShowModal(true);
              }}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              title="Nouvelle catégorie"
            >
              <FiPlus className="mr-2" />
              Nouvelle Catégorie
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Catégories</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FiTag className="text-blue-500 text-2xl" aria-hidden="true" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actives</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <FiEye className="text-green-500 text-2xl" aria-hidden="true" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Inactives</p>
              <p className="text-2xl font-bold text-gray-600">{stats.inactive}</p>
            </div>
            <FiEyeOff className="text-gray-500 text-2xl" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Liste / Grille */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {categories.map((category) => (
            <div
              key={category._id || category.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
            >
              {/* Bandeau photo : permet de reperer d'un coup d'oeil les categories
                  qui n'en ont pas encore (elles sortiraient en aplat sur l'accueil). */}
              {category.image ? (
                <img
                  src={normalizeImagePath(category.image)}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-24 object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-24 bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                  Aucune photo
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  {/* ✅ icône style photo */}
                  <div className="bg-[#fde7ef] p-3 rounded-2xl">
                    {renderCategoryIcon(category.icon, 34, 'text-[#e11d7b]')}
                  </div>

                  <button
                    type="button"
                    onClick={() => handleToggleActive(category._id)}
                    className={`p-1 rounded ${isCatActive(category) ? 'text-green-600' : 'text-gray-400'}`}
                    title={isCatActive(category) ? 'Actif' : 'Inactif'}
                    aria-pressed={isCatActive(category)}
                  >
                    {isCatActive(category) ? <FiEye size={18} /> : <FiEyeOff size={18} />}
                  </button>
                </div>

                <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
                {category.nameAr && (
                  <div
                    dir="rtl"
                    className="text-sm text-gray-500 mb-1"
                    style={{ fontFamily: 'Cairo, sans-serif' }}
                  >
                    {category.nameAr}
                  </div>
                )}
                <div className="mb-2" />
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {category.description || 'Aucune description'}
                </p>

                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-sm text-gray-500">
                    {category.dealsCount || 0} deals
                  </span>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(category)}
                      className="p-1 text-blue-600 hover:text-blue-800"
                      title="Modifier"
                      aria-label={`Modifier ${category.name}`}
                    >
                      <FiEdit size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(category._id)}
                      className="p-1 text-red-600 hover:text-red-800"
                      title="Supprimer"
                      aria-label={`Supprimer ${category.name}`}
                    >
                      <FiTrash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Catégorie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deals
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <tr
                  key={category._id || category.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="bg-[#fde7ef] p-2 rounded-xl mr-3">
                        {renderCategoryIcon(category.icon, 26, 'text-[#e11d7b]')}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-600">{category.description || '-'}</span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{category.dealsCount || 0}</span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isCatActive(category)
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {isCatActive(category) ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        type="button"
                        onClick={() => handleToggleActive(category._id)}
                        className={`${
                          isCatActive(category)
                            ? 'text-gray-600 hover:text-gray-900'
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={isCatActive(category) ? 'Désactiver' : 'Activer'}
                        aria-label={`${isCatActive(category) ? 'Désactiver' : 'Activer'} ${category.name}`}
                      >
                        {isCatActive(category) ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleEdit(category)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Modifier"
                        aria-label={`Modifier ${category.name}`}
                      >
                        <FiEdit size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(category._id)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                        aria-label={`Supprimer ${category.name}`}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t bg-white mt-4 rounded-b">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1 || loading}
            className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
          >
            <FiChevronLeft /> <span>Préc</span>
          </button>

          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages || loading}
            className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
          >
            <span>Suiv</span> <FiChevronRight />
          </button>

          <div className="ml-4 text-sm text-gray-600">
            Page <strong>{page}</strong> / <strong>{totalPages}</strong>
          </div>

          <div className="ml-4 text-sm text-gray-600">
            <span className="mr-2">Afficher</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="border px-2 py-1 rounded"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <span className="ml-2">/ page</span>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          {total > 0 ? `Total: ${total} résultats` : `${categories.length} affichés`}
        </div>
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
        >
          {/* max-h + flex colonne : le formulaire a grandi (photo, nom arabe,
              description arabe) et depassait la hauteur de l'ecran. Sans
              conteneur scrollable, les boutons Annuler / Enregistrer sortaient
              simplement du champ de vision, sans aucun moyen d'y acceder. */}
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden">
            <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
              <div className="p-6 overflow-y-auto flex-1 min-h-0">
                <h2 className="text-2xl font-bold mb-4">
                  {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </h2>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Ex: Restaurants"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom (arabe)
                    </label>
                    <input
                      type="text"
                      dir="rtl"
                      value={formData.nameAr}
                      onChange={(e) => setFormData({ ...formData, nameAr: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      style={{ fontFamily: 'Cairo, sans-serif' }}
                      placeholder="مثال: مطاعم"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Description de la catégorie"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (arabe)
                    </label>
                    <textarea
                      dir="rtl"
                      value={formData.descriptionAr}
                      onChange={(e) => setFormData({ ...formData, descriptionAr: e.target.value })}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      style={{ fontFamily: 'Cairo, sans-serif' }}
                      placeholder="وصف الفئة"
                    />
                  </div>

                  {/* ✅ Select icônes style photo */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Icône
                    </label>

                    <select
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Sélectionner une icône</option>
                      
                      <optgroup label="Promotions / Général">
                        <option value="promotion">Promotion (Tag)</option>
                        <option value="reduction">Réduction (%)</option>
                        <option value="deals">Deals</option>
                        <option value="exclusif">Exclusif</option>
                        <option value="shopping">Shopping</option>
                        <option value="produits">Produits</option>
                        <option value="cadeaux">Cadeaux</option>
                        <option value="favoris">Favoris (Étoile)</option>
                        <option value="vip">VIP (Couronne)</option>
                        <option value="populaire">Populaire</option>
                      </optgroup>
                      
                      <optgroup label="Mode / Vêtements / Accessoires">
                        <option value="mode">Mode / Vêtements</option>
                        <option value="chaussures">Chaussures</option>
                        <option value="chapeaux">Chapeaux</option>
                        <option value="lunettes">Lunettes</option>
                        <option value="accessoires">Accessoires</option>
                        <option value="bijoux">Bijoux</option>
                        <option value="montres">Montres</option>
                      </optgroup>
                      
                      <optgroup label="Restauration">
                        <option value="restaurant">Restaurants</option>
                        <option value="cafe">Café</option>
                        <option value="gastronomie">Gastronomie</option>
                        <option value="fastfood">Fast-food</option>
                        <option value="pizza">Pizza</option>
                        <option value="boulangerie">Boulangerie</option>
                        <option value="patisserie">Pâtisserie</option>
                        <option value="glaces">Glaces</option>
                        <option value="boissons">Boissons</option>
                      </optgroup>
                      
                      <optgroup label="Alimentation / Épicerie">
                        <option value="alimentation">Alimentation</option>
                        <option value="epicerie">Épicerie</option>
                        <option value="fruits">Fruits</option>
                        <option value="legumes">Légumes</option>
                        <option value="bio">Bio</option>
                        <option value="poisson">Poisson</option>
                        <option value="vin">Vin</option>
                      </optgroup>
                      
                      <optgroup label="Santé / Beauté">
                        <option value="spa">Spa</option>
                        <option value="beaute">Beauté</option>
                        <option value="bienetre">Bien-être</option>
                        <option value="coiffure">Coiffure</option>
                        <option value="cosmetiques">Cosmétiques</option>
                        <option value="maquillage">Maquillage</option>
                        <option value="esthetique">Esthétique</option>
                        <option value="laser">Laser</option>
                        <option value="sante">Santé</option>
                        <option value="clinique">Clinique</option>
                        <option value="pharmacie">Pharmacie</option>
                        <option value="dentaire">Dentaire</option>
                        <option value="medecin">Médecin</option>
                        <option value="vaccination">Vaccination</option>
                        <option value="handicap">Accessibilité</option>
                      </optgroup>
                      
                      <optgroup label="Sport / Fitness">
                        <option value="sport">Sport</option>
                        <option value="fitness">Fitness</option>
                        <option value="gym">Salle de sport</option>
                        <option value="running">Running</option>
                        <option value="football">Football</option>
                        <option value="basketball">Basketball</option>
                        <option value="tennis">Tennis</option>
                        <option value="golf">Golf</option>
                        <option value="velo">Vélo</option>
                        <option value="ski">Ski</option>
                        <option value="natation">Natation</option>
                        <option value="baseball">Baseball</option>
                        <option value="volleyball">Volleyball</option>
                        <option value="echecs">Échecs</option>
                      </optgroup>
                      
                      <optgroup label="Voyage / Tourisme">
                        <option value="voyage">Voyage</option>
                        <option value="tourisme">Tourisme</option>
                        <option value="hotel">Hôtel</option>
                        <option value="excursion">Excursion</option>
                        <option value="plage">Plage</option>
                        <option value="croisiere">Croisière</option>
                      </optgroup>
                      
                      <optgroup label="Immobilier / Maison">
                        <option value="immobilier">Immobilier</option>
                        <option value="logement">Logement</option>
                        <option value="maison">Maison</option>
                        <option value="decoration">Décoration</option>
                        <option value="meubles">Meubles</option>
                        <option value="literie">Literie</option>
                        <option value="salle_de_bain">Salle de bain</option>
                        <option value="eclairage">Éclairage</option>
                        <option value="jardin">Jardin</option>
                        <option value="bricolage">Bricolage</option>
                        <option value="outillage">Outillage</option>
                        <option value="peinture">Peinture</option>
                      </optgroup>
                      
                      <optgroup label="Loisirs / Culture">
                        <option value="loisirs">Loisirs</option>
                        <option value="divertissement">Divertissement</option>
                        <option value="musique">Musique</option>
                        <option value="cinema">Cinéma</option>
                        <option value="jeux">Jeux vidéo</option>
                        <option value="jouets">Jouets</option>
                        <option value="enfants">Enfants</option>
                        <option value="photo">Photo</option>
                        <option value="guitare">Guitare</option>
                        <option value="concert">Concert</option>
                        <option value="spectacle">Spectacle</option>
                        <option value="art">Art</option>
                        <option value="des">Jeux de société</option>
                      </optgroup>
                      
                      <optgroup label="Éducation">
                        <option value="education">Éducation</option>
                        <option value="formation">Formation</option>
                        <option value="cours">Cours</option>
                        <option value="livres">Livres</option>
                        <option value="langues">Langues</option>
                        <option value="ecriture">Écriture</option>
                        <option value="science">Science</option>
                        <option value="recherche">Recherche</option>
                      </optgroup>
                      
                      <optgroup label="Voiture / Transport">
                        <option value="voiture">Voiture</option>
                        <option value="automobile">Automobile</option>
                        <option value="location_voiture">Location voiture</option>
                        <option value="moto">Moto</option>
                        <option value="taxi">Taxi</option>
                        <option value="transport">Transport / Bus</option>
                        <option value="camion">Camion</option>
                        <option value="tracteur">Tracteur</option>
                        <option value="metro">Métro</option>
                        <option value="bateau">Bateau</option>
                      </optgroup>
                      
                      <optgroup label="Électronique / Informatique">
                        <option value="electronique">Électronique</option>
                        <option value="gadgets">Gadgets</option>
                        <option value="tv">TV</option>
                        <option value="informatique">Informatique</option>
                        <option value="bureautique">Bureautique</option>
                        <option value="ordinateur">Ordinateur</option>
                        <option value="clavier">Clavier</option>
                        <option value="souris">Souris</option>
                        <option value="stockage">Stockage</option>
                        <option value="serveur">Serveur</option>
                        <option value="reseau">Réseau / WiFi</option>
                        <option value="batterie">Batterie</option>
                        <option value="chargeur">Chargeur</option>
                      </optgroup>
                      
                      <optgroup label="Téléphonie / Technologie">
                        <option value="technologie">Technologie</option>
                        <option value="mobile">Mobile</option>
                        <option value="smartphones">Smartphones</option>
                        <option value="tablette">Tablette</option>
                        <option value="simcard">Carte SIM</option>
                        <option value="robot">Robot / IA</option>
                      </optgroup>
                      
                      <optgroup label="Animaux / Nature">
                        <option value="animaux">Animaux</option>
                        <option value="chien">Chien</option>
                        <option value="chat">Chat</option>
                        <option value="oiseaux">Oiseaux</option>
                        <option value="nature">Nature</option>
                        <option value="fleurs">Fleurs</option>
                        <option value="plantes">Plantes</option>
                      </optgroup>
                      
                      <optgroup label="Services / Business">
                        <option value="services">Services</option>
                        <option value="business">Business</option>
                        <option value="professionnel">Professionnel</option>
                        <option value="rendezvous">Rendez-vous</option>
                        <option value="boutique">Boutique</option>
                        <option value="magasin">Magasin</option>
                        <option value="entrepot">Entrepôt</option>
                        <option value="industrie">Industrie</option>
                        <option value="partenaire">Partenaire</option>
                        <option value="marketing">Marketing</option>
                        <option value="statistiques">Statistiques</option>
                        <option value="presse">Presse</option>
                        <option value="video">Vidéo</option>
                        <option value="podcast">Podcast</option>
                        <option value="photographie">Photographie</option>
                      </optgroup>
                      
                      <optgroup label="Divers / Confort">
                        <option value="luxe">Luxe</option>
                        <option value="premium">Premium</option>
                        <option value="climatisation">Climatisation</option>
                        <option value="chauffage">Chauffage</option>
                        <option value="froid">Froid</option>
                        <option value="meteo">Météo</option>
                        <option value="energie">Énergie</option>
                        <option value="international">International</option>
                        <option value="localisation">Localisation</option>
                        <option value="conciergerie">Conciergerie</option>
                        <option value="coeur">Coeur</option>
                      </optgroup>
                      
                      <optgroup label="Paiement / Sécurité / Support">
                        <option value="paiement">Paiement</option>
                        <option value="securite">Sécurité</option>
                        <option value="support">Support</option>
                      </optgroup>
                      
                      <optgroup label="Autre">
                        <option value="autre">Autre</option>
                      </optgroup>
                    </select>

                    {formData.icon ? (
                      <div className="mt-2 flex items-center gap-2 text-sm text-gray-600">
                        <span>Aperçu :</span>
                        <div className="bg-[#fde7ef] p-2 rounded-xl">
                          {renderCategoryIcon(formData.icon, 28, 'text-[#e11d7b]')}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Photo de la categorie — alimente les 4 tuiles « Comment ça
                      marche » de l'accueil quand aucune publicite n'est
                      configuree sur l'emplacement home_tiles. */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Photo de la catégorie
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Format recommandé : <strong>600 × 800 px</strong> (ratio 3:4, portrait).
                      JPG/WebP/PNG, 5 Mo maximum. Elle s'affiche en fond des 4 tuiles de
                      l'accueil, sous un voile coloré — gardez le sujet bien centré.
                    </p>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-24 rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center border"
                        style={{ aspectRatio: '3 / 4' }}
                      >
                        {imagePreview ? (
                          <img src={imagePreview} alt="aperçu" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs text-gray-400 px-2 text-center">Aucune photo</span>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="px-3 py-2 rounded-lg border text-sm inline-flex items-center gap-2 hover:bg-gray-50"
                        >
                          <FiUpload size={14} /> Choisir une photo
                        </button>
                        {imagePreview ? (
                          <button
                            type="button"
                            onClick={clearImage}
                            className="px-3 py-2 rounded-lg border border-red-200 text-sm text-red-600 inline-flex items-center gap-2 hover:bg-red-50"
                          >
                            <FiX size={14} /> Retirer la photo
                          </button>
                        ) : null}
                      </div>
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={pickImage}
                        className="hidden"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="active"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                      Catégorie active
                    </label>
                  </div>
                </div>
              </div>

              {/* shrink-0 : le pied ne doit jamais etre comprime ni defiler
                  avec le contenu, c'est le seul acces au bouton Enregistrer. */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-lg border-t shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategory(null);
                    resetImageState();
                    setFormData({ name: '', nameAr: '', description: '', descriptionAr: '', icon: '', active: true });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 ${
                    isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  {editingCategory
                    ? isSubmitting
                      ? 'Mise à jour...'
                      : 'Mettre à jour'
                    : isSubmitting
                    ? 'Création...'
                    : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategoriesManagement;
